// Pleasant nudge — weekly "good drive day" notifications (#69, #195, #198).
//
// Schedules a weekly local notification on each day the user picks, at a
// time of their choosing. Off by default — testers explicitly opt in via
// Settings → Pleasant nudge.
//
// History:
//   #69  — weekend-only (Saturday/Sunday), fixed 9 AM
//   #195 — any day of the week, still single day + fixed 9 AM
//   #198 — multi-day + custom time
//
// Storage is back-compat. Old `{ day: "saturday" }` is migrated to the
// new shape on read. Storage key intentionally unchanged so existing
// users keep their settings without prompting again.
//
// Implementation: @capacitor/local-notifications. iOS handles scheduling
// on-device — no server, no APNs, no App Store Connect config. The only
// iOS cost is the notification permission ask, which we prime before
// triggering so the user has context.
//
// One notification scheduled per selected day. Notification IDs are
// derived from a base + iOS weekday number so we can cancel individual
// days without nuking the rest.

import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";

const KEY = "pleasant.weekendNudge.v1";
/** Base for per-day notification IDs. iOS weekday added to get the actual
 *  per-notification ID (so Sun = 7202, Mon = 7203, ..., Sat = 7208). */
const BASE_NOTIFICATION_ID = 7201;

/** A single day of the week. The legacy "off" value is no longer part of
 *  the type — "off" is now represented by an empty `days` array. */
export type WeekDay =
  | "sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday";

/** Back-compat alias: callers that still import NudgeDay get the same
 *  union, plus the legacy "off" sentinel so old code keeps compiling. */
export type NudgeDay = WeekDay | "off";

/** Map our day strings to iOS NSCalendar weekday numbers (Sun=1..Sat=7). */
const WEEKDAY_NUM: Record<WeekDay, number> = {
  sunday: 1, monday: 2, tuesday: 3, wednesday: 4,
  thursday: 5, friday: 6, saturday: 7,
};

const WEEK_DAYS: WeekDay[] = [
  "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
];

const isWeekDay = (v: unknown): v is WeekDay =>
  typeof v === "string" && (WEEK_DAYS as readonly string[]).includes(v);

interface Stored {
  /** Empty array = nudge is off. */
  days: WeekDay[];
  /** 0-23. Default 9 (= 9 AM local). */
  hour: number;
  /** 0-59. Default 0. */
  minute: number;
}

const DEFAULT_STORED: Stored = { days: [], hour: 9, minute: 0 };

/** Rotating set of warm prompts. Picked by week-of-year so each nudge
 *  feels fresh instead of repeating the same line forever. */
const PROMPTS: Array<{ title: string; body: string }> = [
  {
    title: "A good day to wander",
    body: "Want to take the long way somewhere today?",
  },
  {
    title: "Pleasant Routes",
    body: "Anywhere you'd take the pretty road today?",
  },
  {
    title: "The journey is the point",
    body: "A scenic trip is a small luxury. Take one if you can.",
  },
  {
    title: "Pleasant mode",
    body: "A nicer route to wherever you're going — it's why this app exists.",
  },
];

const readStored = (): Stored => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_STORED;
    const p = JSON.parse(raw);
    if (!p || typeof p !== "object") return DEFAULT_STORED;

    // New shape (post-#198).
    if (Array.isArray(p.days)) {
      const days = p.days.filter(isWeekDay) as WeekDay[];
      const hour = typeof p.hour === "number" && p.hour >= 0 && p.hour <= 23 ? p.hour : 9;
      const minute = typeof p.minute === "number" && p.minute >= 0 && p.minute <= 59 ? p.minute : 0;
      return { days, hour, minute };
    }
    // Legacy shape (#69, #195) — { day: "saturday" | "off" | ... }.
    if (typeof p.day === "string") {
      if (p.day === "off") return DEFAULT_STORED;
      if (isWeekDay(p.day)) return { days: [p.day], hour: 9, minute: 0 };
    }
  } catch { /* ignore */ }
  return DEFAULT_STORED;
};

const writeStored = (s: Stored): void => {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* ignore */ }
};

/** Current selected days (empty = off). */
export const getNudgeDays = (): WeekDay[] => readStored().days;
/** Current time of day for the nudge as [hour, minute]. */
export const getNudgeTime = (): { hour: number; minute: number } => {
  const s = readStored();
  return { hour: s.hour, minute: s.minute };
};

/** Legacy single-day getter — returns the first selected day or "off". */
export const getNudgeDay = (): NudgeDay => readStored().days[0] ?? "off";

/** Has the user actually granted iOS notification permission? */
export const checkNudgePermission = async (): Promise<"granted" | "denied" | "prompt" | "unknown"> => {
  if (!Capacitor.isNativePlatform()) return "unknown";
  try {
    const res = await LocalNotifications.checkPermissions();
    if (res.display === "granted") return "granted";
    if (res.display === "denied") return "denied";
    return "prompt";
  } catch {
    return "unknown";
  }
};

/** Request notification permission. Returns the result so callers can
 *  decide whether to actually schedule. */
export const requestNudgePermission = async (): Promise<"granted" | "denied" | "prompt"> => {
  if (!Capacitor.isNativePlatform()) return "denied";
  try {
    const res = await LocalNotifications.requestPermissions();
    if (res.display === "granted") return "granted";
    if (res.display === "denied") return "denied";
    return "prompt";
  } catch {
    return "denied";
  }
};

/** Pick the prompt for the current week. Rotation keeps the nudge from
 *  feeling like the same broken record. */
const pickPrompt = (date: Date): { title: string; body: string } => {
  const weekNum = Math.floor(date.getTime() / (1000 * 60 * 60 * 24 * 7));
  return PROMPTS[weekNum % PROMPTS.length];
};

/** Cancel every possible Pleasant-nudge notification. Cheaper than tracking
 *  which IDs are live — there are only 7 candidates. */
const cancelExisting = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await LocalNotifications.cancel({
      notifications: WEEK_DAYS.map((d) => ({
        id: BASE_NOTIFICATION_ID + WEEKDAY_NUM[d],
      })),
    });
  } catch { /* ignore */ }
};

/** Schedule the next set of Pleasant nudges based on stored selection.
 *  Empty `days` array = clear and done. */
export const scheduleNudge = async (): Promise<void> => {
  const { days, hour, minute } = readStored();
  await cancelExisting();
  if (days.length === 0) return;
  if (!Capacitor.isNativePlatform()) return;
  const prompt = pickPrompt(new Date());
  try {
    await LocalNotifications.schedule({
      notifications: days.map((d) => {
        const weekday = WEEKDAY_NUM[d];
        return {
          id: BASE_NOTIFICATION_ID + weekday,
          title: prompt.title,
          body: prompt.body,
          schedule: {
            on: { weekday, hour, minute },
            allowWhileIdle: true,
          },
        };
      }),
    });
  } catch {
    /* schedule failed — silent. User will see the current state next
     *  time they open Settings. */
  }
};

/** Persist days + time and re-schedule iOS notifications. */
export const setNudgeSchedule = async (
  days: WeekDay[],
  hour: number,
  minute: number,
): Promise<void> => {
  writeStored({ days, hour, minute });
  await scheduleNudge();
};

/** Legacy single-day setter — kept so existing callers (if any) keep
 *  working. Maps "off" → empty array. */
export const setNudgeDay = async (day: NudgeDay): Promise<void> => {
  const cur = readStored();
  if (day === "off") {
    await setNudgeSchedule([], cur.hour, cur.minute);
  } else {
    await setNudgeSchedule([day], cur.hour, cur.minute);
  }
};
